import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { ASSETS } from '../../BaseURL';
import { useFacility } from '../../context/FacilityContext';
import Overview from './Overview';
import Branches from './Branches';
import UsersAccess from './UsersAccess';
import Customers from './Customers';
import Invoices from './Invoices';
import Products from './Products';
import POS from './POS';
import Sales from './Sales';
import Inventory from './Inventory';
import Suppliers from './Suppliers';
import Purchases from './Purchases';
import Transfers from './Transfers';
import Expenses from './Expenses';
import AuditTrail from './AuditTrail';
import Reports from './Reports';
import Attendance from './Attendance';
import HREmployees from './HREmployees';
import Settings from './Settings';
import useInactivityLogout from '../../hooks/useInactivityLogout';
import '../../styles/dashboard.css';

// Role → which nav items are visible. super_admin sees everything and is
// implicit (any item without a matching entry falls through). When a role is
// listed, the item appears; otherwise it's hidden.
//   - `roles: ['*']` is the same as showing to everyone.
//   - The backend enforces the same rules independently, so hiding here is
//     UX polish — someone who guesses the URL is still bounced by the API.
const NAV = [
  { to: '/management',            icon: '📊', label: 'Overview',      end: true, roles: ['*'] },
  { to: '/management/pos',        icon: '🛒', label: 'Point of Sale', roles: ['cashier', 'store_officer', 'branch_manager'] },
  { to: '/management/sales',      icon: '💵', label: 'Sales',         roles: ['cashier', 'accountant', 'branch_manager'] },
  { to: '/management/products',   icon: '📦', label: 'Products',      roles: ['store_officer', 'branch_manager'] },
  { to: '/management/inventory',  icon: '🗃️', label: 'Inventory',     roles: ['store_officer', 'branch_manager'] },
  { to: '/management/transfers',  icon: '🔁', label: 'Stock Transfers', roles: ['store_officer', 'branch_manager'] },
  { to: '/management/customers',  icon: '👥', label: 'Customers',     roles: ['cashier', 'accountant', 'store_officer', 'branch_manager'] },
  { to: '/management/invoices',   icon: '🧾', label: 'Invoices',      roles: ['accountant', 'store_officer', 'branch_manager'] },
  { to: '/management/suppliers',  icon: '🚚', label: 'Suppliers',     roles: ['store_officer', 'branch_manager'] },
  { to: '/management/purchases',  icon: '📥', label: 'Purchases',     roles: ['store_officer', 'branch_manager'] },
  { to: '/management/expenses',   icon: '💳', label: 'Expenses',      roles: ['accountant', 'branch_manager'] },
  { to: '/management/reports',    icon: '📈', label: 'Reports',       roles: ['accountant', 'branch_manager'] },
  { to: '/management/attendance', icon: '👉', label: 'Attendance',    roles: ['branch_manager', 'accountant'] },
  { to: '/management/employees',  icon: '🪪', label: 'Employees',      roles: ['branch_manager'] },
  { to: '/management/branches',   icon: '🏬', label: 'Branches',       roles: [] },   // super only
  { to: '/management/users',      icon: '🪪', label: 'Users & Access', roles: ['branch_manager'] },
  { to: '/management/audit',      icon: '📜', label: 'Audit Trail',   roles: ['branch_manager'] },
  { to: '/management/settings',   icon: '⚙️', label: 'Settings',      roles: [] },   // super only
];

// Same map, keyed by URL, for the Route guard below.
const ROUTE_ROLES = Object.fromEntries(NAV.map((n) => [n.to, n.roles]));

const canSee = (roleList, userRole) => {
  if (!userRole) return false;
  if (userRole === 'super_admin') return true;
  if (!roleList) return false;
  if (roleList.includes('*')) return true;
  return roleList.includes(userRole);
};

// Wraps a page in a role check. If the user doesn't have access, they're
// bounced back to /management (Overview) instead of seeing the page shell.
const Guarded = ({ path, me, children }) => {
  const roles = ROUTE_ROLES[`/management${path ? `/${path}` : ''}`];
  if (!me) return null;
  if (!canSee(roles, me.role)) return <Navigate to="/management" replace />;
  return children;
};

const Placeholder = ({ title }) => (
  <div className="panel">
    <div className="panel-head"><h2>{title}</h2></div>
    <p style={{ color: 'var(--mute)', margin: 0 }}>
      Module coming online soon. The data model, routes and permissions for this section will be wired in the next iteration.
    </p>
  </div>
);

const today = () => new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

const THEMES = [
  { id: 'light',    label: 'Light' },
  { id: 'slate',    label: 'Slate' },
  { id: 'midnight', label: 'Midnight' },
];
const THEME_KEY = 'posSideTheme';

const ManagementDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  useInactivityLogout(10 * 60 * 1000);
  const { facility } = useFacility();
  const [me, setMe] = useState(null);
  const [sideOpen, setSideOpen] = useState(false);
  const [theme, setTheme] = useState(() => {
    try {
      const v = localStorage.getItem(THEME_KEY);
      return THEMES.some((t) => t.id === v) ? v : 'slate';
    } catch (_) { return 'slate'; }
  });
  const [themeOpen, setThemeOpen] = useState(false);
  const themeRef = useRef(null);

  const currentTitle = (() => {
    const path = location.pathname;
    const match = NAV.find((n) => path === n.to || (!n.end && path.startsWith(n.to + '/')));
    return match?.label || 'Dashboard';
  })();

  useEffect(() => {
    try { setMe(JSON.parse(localStorage.getItem('user') || 'null')); } catch (_) {}
  }, []);

  const firstName = (me?.full_name || '').trim().split(/\s+/)[0] || 'there';
  // The login response already carries branch_name (JOINed from branches).
  // We only fall back to the "All branches" pill for a super admin who genuinely
  // has no branch_id assigned in the database.
  const branchLabel = me?.branch_name || null;
  const isSuperNoBranch = me?.role === 'super_admin' && !me?.branch_id;

  // Close mobile drawer on navigation
  useEffect(() => { setSideOpen(false); }, [location.pathname]);

  // Persist theme + apply to <html> so it cascades to every page
  useEffect(() => {
    try { localStorage.setItem(THEME_KEY, theme); } catch (_) {}
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Close theme menu when clicking outside
  useEffect(() => {
    if (!themeOpen) return undefined;
    const onDoc = (e) => { if (themeRef.current && !themeRef.current.contains(e.target)) setThemeOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [themeOpen]);

  const signOut = () => {
    localStorage.removeItem('user');
    navigate('/login', { replace: true });
  };

  return (
    <div className="dash-shell">
      <div className={`dash-side-backdrop ${sideOpen ? 'open' : ''}`} onClick={() => setSideOpen(false)} />
      <aside className={`dash-side ${sideOpen ? 'open' : ''}`}>
        <div className="side-brand">
          {facility?.logo ? (
            <img src={`${ASSETS}${facility.logo}`} alt={facility.name} className="side-logo" />
          ) : (
            <span className="mark">{(facility?.name?.[0] || 'P').toUpperCase()}</span>
          )}
          <span className="name">{facility?.name || 'POS Ghana'}</span>
        </div>
        <div className="side-user">
          {me ? <><b>{me.full_name}</b>{me.role}</> : <b>Signed in</b>}
        </div>
        <nav className="side-nav">
          {NAV
            .filter((n) => canSee(n.roles, me?.role))
            .map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              >
                <span className="nav-icon">{n.icon}</span>
                <span>{n.label}</span>
              </NavLink>
            ))}
        </nav>
        <div className="side-foot">
          <button onClick={signOut}>Sign out</button>
        </div>
      </aside>

      <main className="dash-main">
        <header className="dash-top">
          <button className="dash-side-toggle" onClick={() => setSideOpen(true)} aria-label="Open menu">☰</button>
          <h1>{currentTitle}</h1>
          {me && (
            <div className="dash-welcome" title={isSuperNoBranch ? 'Global access — all branches' : `Signed in at ${branchLabel || 'this branch'}`}>
              <span className="hello">Welcome, <strong>{firstName}</strong></span>
              <span className={`branch-pill ${isSuperNoBranch ? 'super' : ''}`}>
                {isSuperNoBranch
                  ? <>🏛️ <span>All branches</span></>
                  : <>📍 <span>{branchLabel || 'No branch assigned'}</span></>}
              </span>
            </div>
          )}
          <div className="grow" />
          <div className="date">{today()}</div>
          <div className="theme-switcher" ref={themeRef}>
            <button
              className="theme-toggle"
              onClick={() => setThemeOpen((o) => !o)}
              aria-label="Change sidebar theme"
              aria-haspopup="menu"
              aria-expanded={themeOpen}
            >
              <span className={`swatch theme-${theme}`} />
              <span>Theme</span>
              <span className="caret">▾</span>
            </button>
            {themeOpen && (
              <div className="theme-menu" role="menu">
                {THEMES.map((t) => (
                  <button
                    key={t.id}
                    role="menuitemradio"
                    aria-checked={theme === t.id}
                    className={`theme-opt ${theme === t.id ? 'active' : ''}`}
                    onClick={() => { setTheme(t.id); setThemeOpen(false); }}
                  >
                    <span className={`swatch theme-${t.id}`} />
                    <span>{t.label}</span>
                    {theme === t.id && <span className="check">✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </header>
        <div className="dash-body">
          <Routes>
            <Route index element={<Overview facility={facility} me={me} />} />
            <Route path="pos"        element={<Guarded path="pos" me={me}><POS /></Guarded>} />
            <Route path="sales"      element={<Guarded path="sales" me={me}><Sales /></Guarded>} />
            <Route path="products"   element={<Guarded path="products" me={me}><Products /></Guarded>} />
            <Route path="inventory"  element={<Guarded path="inventory" me={me}><Inventory /></Guarded>} />
            <Route path="transfers"  element={<Guarded path="transfers" me={me}><Transfers /></Guarded>} />
            <Route path="customers"  element={<Guarded path="customers" me={me}><Customers /></Guarded>} />
            <Route path="invoices"   element={<Guarded path="invoices" me={me}><Invoices /></Guarded>} />
            <Route path="suppliers"  element={<Guarded path="suppliers" me={me}><Suppliers /></Guarded>} />
            <Route path="purchases"  element={<Guarded path="purchases" me={me}><Purchases /></Guarded>} />
            <Route path="expenses"   element={<Guarded path="expenses" me={me}><Expenses /></Guarded>} />
            <Route path="reports"    element={<Guarded path="reports" me={me}><Reports /></Guarded>} />
            <Route path="attendance" element={<Guarded path="attendance" me={me}><Attendance /></Guarded>} />
            <Route path="employees"  element={<Guarded path="employees" me={me}><HREmployees /></Guarded>} />
            <Route path="branches"   element={<Guarded path="branches" me={me}><Branches /></Guarded>} />
            <Route path="users"      element={<Guarded path="users" me={me}><UsersAccess /></Guarded>} />
            <Route path="audit"      element={<Guarded path="audit" me={me}><AuditTrail /></Guarded>} />
            <Route path="settings"   element={<Guarded path="settings" me={me}><Settings me={me} /></Guarded>} />
            <Route path="*" element={<Navigate to="/management" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

export default ManagementDashboard;
